import {
  Button,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MultiSelectField,
  SelectField,
  useComputed,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useEffect } from "react";
import { useSetState } from "react-use";
import { ItemsBoundSelectField } from "src/components/autoPopulateSelects/ItemsBoundSelectField";
import { LocationPathField } from "src/components/locationPathField/LocationPathField";
import { CreateDesignPackagePlaceholderTliInput, ItemTakeoffType } from "src/generated/graphqlTypes";
import { DesignPackageItemSlotEditorDocument } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageItemSlotEditor.generated";
import { DesignPackageSlotsDocument } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageSlotsLeftNav.generated";
import {
  useAddNewDesignPackageSlotModal_CreateSlotMutation,
  useAddNewDesignPackageSlotModal_MaterialAttributeDimensionsQuery,
  useAddNewDesignPackageSlotModalQuery,
} from "src/routes/libraries/design-catalog/design-package-configurator/routes/new/AddNewDesignPackageSlotModal.generated";

type AddNewDesignPackageSlotModalProps = {
  designPackageId: string;
  rpavId: string;
  setCtxState: (partial: { slotIds: string[] }) => void;
  /** Pre-selects a location when triggered from a per-subgroup entry point. */
  initialLocationId?: string;
};

export function AddNewDesignPackageSlotModal(props: AddNewDesignPackageSlotModalProps) {
  const { designPackageId, rpavId, setCtxState, initialLocationId } = props;
  const { closeModal } = useModal();

  const { data } = useAddNewDesignPackageSlotModalQuery({ variables: { dpId: designPackageId } });
  const planPackages = data?.designPackage.planPackages ?? [];
  const dpLocationCode = data?.designPackage.location.code;

  const formState = useFormState({
    config: formConfig,
    init: {
      input: {
        locationId: initialLocationId ?? undefined,
        locationPath: undefined,
        itemId: undefined,
        customSlotName: undefined,
        planPackageIds: [],
      },
      map: (input) => input,
    },
  });

  const itemId = useComputed(() => formState.itemId.value, [formState]);

  const madsQuery = useAddNewDesignPackageSlotModal_MaterialAttributeDimensionsQuery({
    variables: { filter: { itemId, useInTakeoff: true } },
    skip: !itemId,
  });

  const [mavs, setMavs] = useSetState<Record<string, string>>({});
  useEffect(() => setMavs((prev) => Object.entries(prev).mapToObject(([k]) => [k, undefined])), [itemId, setMavs]);

  const [createSlot] = useAddNewDesignPackageSlotModal_CreateSlotMutation({
    refetchQueries: [DesignPackageItemSlotEditorDocument, DesignPackageSlotsDocument],
  });

  async function onAddSlot() {
    const locationPath = formState.locationPath.value;
    const { data } = await createSlot({
      variables: {
        input: {
          readyPlanId: designPackageId,
          itemId: formState.itemId.value,
          // The BE accepts exactly one of `locationPath` (a new feature under a parent) or `locationId` (existing location)
          ...(locationPath ? { locationPath } : { locationId: formState.locationId.value }),
          customSlotName: formState.customSlotName.value,
          attributeValueIds: Object.values(mavs).compact(),
          applicablePlanPackageIds: formState.planPackageIds.value,
        },
      },
    });
    const newSlotId = data?.createDesignPackagePlaceholderTli.placeholderTli.slot.id;
    if (newSlotId) setCtxState({ slotIds: [newSlotId] });
    closeModal();
  }

  return (
    <>
      <ModalHeader>Add a new slot</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <div css={Css.sm.gray700.$}>
            Select a room or a feature of a room for your new slot. If the feature of the room does not exist, choose
            the most specific location and then add the feature via the plus sign.
          </div>
          {planPackages.nonEmpty && (
            <MultiSelectField
              label="Plans"
              values={formState.planPackageIds.value ?? []}
              options={planPackages}
              getOptionValue={(pp) => pp.id}
              getOptionLabel={(pp) => pp.name}
              onSelect={(ids) => formState.planPackageIds.set(ids)}
              nothingSelectedText="All linked plans"
            />
          )}
          <LocationPathField
            field={formState.locationId}
            newLocationPathField={formState.locationPath}
            filter={{ rpav: [rpavId] }}
          />
          {dpLocationCode && (
            <ItemsBoundSelectField
              label="Placeholder type"
              field={formState.itemId}
              filter={{
                version: [2],
                designPackageLocation: dpLocationCode,
                takeoffType: [ItemTakeoffType.Placeholder],
              }}
            />
          )}
          {madsQuery.loading ? (
            <div css={Css.sm.gray500.$}>Fetching dimensions...</div>
          ) : (
            madsQuery.data?.materialAttributeDimensions.map((mad) => (
              <SelectField
                key={mad.id}
                label={mad.name}
                unsetLabel="Any"
                options={mad.values ?? []}
                getOptionValue={(mav) => mav.id}
                getOptionLabel={(mav) => mav?.textValue ?? ""}
                value={mavs[mad.id]}
                onSelect={(mavId) => setMavs({ [mad.id]: mavId })}
              />
            ))
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={closeModal} />
        <Observer>
          {() => <Button label="Add slot" variant="primary" disabled={!formState.valid} onClick={onAddSlot} />}
        </Observer>
      </ModalFooter>
    </>
  );
}

const formConfig: ObjectConfig<
  Pick<CreateDesignPackagePlaceholderTliInput, "itemId" | "locationId" | "locationPath" | "customSlotName"> & {
    planPackageIds: string[];
  }
> = {
  itemId: { type: "value", rules: [required] },
  locationId: { type: "value", rules: [required] },
  locationPath: { type: "value" },
  customSlotName: { type: "value" },
  planPackageIds: { type: "value" },
};
